import React, { useState, useMemo } from 'react';
import {
  Users, UserPlus, Stethoscope, Calendar, Activity, Trash2, IndianRupee, ClipboardList,
  MessageSquare, X, Send, CheckCircle2, AlertTriangle,
} from 'lucide-react';
import { toast } from 'react-toastify';
import {
  LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, BarChart, Bar, Legend,
} from 'recharts';
import { useDashboardQuery, useLeadFunnelQuery, useLeadSourcesQuery } from '../../api/reportsApi';
import { useListAppointmentsQuery } from '../../api/appointmentsApi';
import { useSendDueRemindersMutation } from '../../api/communicationsApi';
import { apiErrorMessage } from '../../utils/apiError';
import ChartBox from '../../components/ChartBox';
import DateRangePicker, { useDateRange } from '../../components/DateRangePicker';
import HomeoToggle, { useExcludeHomeopathy } from '../../components/HomeoToggle';

// KPI cards — all values are scoped to the selected date range by the backend.
//
// pendingDues deliberately does NOT appear here: it's a clinic-wide snapshot of money currently
// owed (no date filter on the backend), not something that resets or accumulates over a chosen
// range. Showing it under "Period Metrics" — next to a rangeLabel implying every card here is
// scoped to it — read as broken, since changing the date range visibly did nothing to it. It's
// still shown, honestly, under "Today's Snapshot" (OPS_DEFS) as a current-status figure.
const KPI_DEFS = [
  { key: 'totalLeads',               label: 'Total Leads',         icon: UserPlus,      color: 'var(--blue-500)',   bg: 'var(--blue-50)' },
  { key: 'qualifiedLeads',           label: 'Qualified Leads',     icon: ClipboardList, color: 'var(--green-600)',  bg: 'var(--green-50)' },
  { key: 'junkLeads',                label: 'Junk Leads',          icon: Trash2,        color: 'var(--red-500)',    bg: 'var(--red-50)' },
  { key: 'consultationsThisMonth',   label: 'Consultations',       icon: Stethoscope,   color: 'var(--purple-600)', bg: 'var(--purple-100)' },
  { key: 'activePatients',           label: 'Active Patients',     icon: Users,         color: 'var(--teal-600)',   bg: 'var(--teal-100)' },
  { key: 'activeTreatmentPlans',     label: 'Active Treatments',   icon: Activity,      color: 'var(--blue-700)',   bg: 'var(--blue-100)' },
  { key: 'upcomingAppointments',     label: 'Upcoming Appts',      icon: Calendar,      color: 'var(--amber-600)',  bg: 'var(--amber-100)' },
  { key: 'sessionsCompletedThisMonth', label: 'Sessions Done',     icon: Activity,      color: 'var(--green-700)',  bg: 'var(--green-100)' },
];

const fmtCurrency = (v) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(v ?? 0);

// Today-fixed operational cards — these always reflect the CURRENT day, not the range picker.
const OPS_DEFS = [
  { key: 'todaysAppointments', label: "Appointments",        icon: Calendar,    color: 'var(--blue-700)',  bg: 'var(--blue-100)' },
  { key: 'sessionsDueToday',   label: 'Sessions Due',        icon: Activity,    color: 'var(--amber-600)', bg: 'var(--amber-100)' },
  { key: 'newLeadsToday',      label: 'New Leads',           icon: UserPlus,    color: 'var(--green-600)', bg: 'var(--green-50)' },
  { key: 'activePatients',     label: 'Active Patients',     icon: Users,       color: 'var(--teal-600)',  bg: 'var(--teal-100)' },
  { key: 'pendingDues',        label: 'Outstanding Payments',icon: IndianRupee, color: 'var(--red-700)',   bg: 'var(--red-50)', currency: true },
];

// Build a short human-readable label for a date range object { from, to }.
function buildRangeLabel(from, to) {
  const fmt = (iso) => iso ? new Date(iso).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata',  day: 'numeric', month: 'short', year: 'numeric' }) : null;
  const f = fmt(from);
  const t = fmt(to);
  if (f && t) return `${f} – ${t}`;
  if (f) return `From ${f}`;
  if (t) return `Until ${t}`;
  return 'All time';
}

export default function DashboardPage() {
  const dr = useDateRange('30d');
  const [excludeHomeo] = useExcludeHomeopathy();
  const [showReminder, setShowReminder] = useState(false);

  const rangeArgs = useMemo(
    () => ({ ...dr.range, ...(excludeHomeo ? { excludeHomeopathy: true } : {}) }),
    [dr.range, excludeHomeo],
  );

  // Single dashboard call for KPIs + revenue chart (all range-scoped).
  const { data, isLoading, isError, refetch } = useDashboardQuery(rangeArgs);

  // Dedicated range-scoped calls for charts that have their own endpoints.
  const { data: funnelData,   isFetching: funnelLoading  } = useLeadFunnelQuery(dr.range);
  const { data: sourcesData,  isFetching: sourcesLoading } = useLeadSourcesQuery(dr.range);

  const rangeLabel = useMemo(
    () => buildRangeLabel(dr.range.from, dr.range.to),
    [dr.range.from, dr.range.to],
  );

  const kpiSkeleton = <span className="skeleton" style={{ display: 'inline-block', width: 80, height: 22 }} />;

  return (
    <>
      <div className="page-header">
        <div className="breadcrumb"><span>Home</span><span className="bc-sep">/</span><span className="bc-cur">Dashboard</span></div>
        <div className="page-header-row">
          <div>
            <h1>Dashboard</h1>
            <div className="page-header-sub">
              Showing data for <strong>{rangeLabel}</strong>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <button className="btn btn-success btn-sm" style={{ gap: 6 }} onClick={() => setShowReminder(true)}>
              <MessageSquare size={14} /> Send Reminder
            </button>
            <HomeoToggle />
            <DateRangePicker
              preset={dr.preset} onPresetChange={dr.setPreset}
              customFrom={dr.customFrom} customTo={dr.customTo} onCustomChange={dr.onCustomChange}
            />
          </div>
        </div>
      </div>

      {isError && (
        <div className="auth-error" style={{ marginBottom: 14 }}>
          Could not load dashboard. Is the API running?{' '}
          <button className="btn btn-sm btn-outline" onClick={refetch}>Retry</button>
        </div>
      )}

      {/* ── Today's fixed operational snapshot (always reflects current day) ── */}
      <div style={{ marginBottom: 6 }}>
        <span style={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: 'var(--text-muted)' }}>
          Today's Snapshot
        </span>
      </div>
      <div className="kpi-grid" style={{ marginBottom: 22 }}>
        {OPS_DEFS.map(({ key, label, icon: Icon, color, bg, currency }) => (
          <div key={key} className="kpi-card" style={{ '--kpi-accent': color, '--kpi-bg': bg }}>
            <div className="kpi-top"><div className="kpi-icon"><Icon size={18} /></div></div>
            <div className="kpi-value">
              {isLoading ? kpiSkeleton : currency ? fmtCurrency(data?.[key]) : (data?.[key] ?? 0).toLocaleString()}
            </div>
            <div className="kpi-label">{label}</div>
          </div>
        ))}
      </div>

      {/* ── Range-scoped KPIs ── */}
      <div style={{ marginBottom: 6, display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: 'var(--text-muted)' }}>
          Period Metrics
        </span>
        <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>
          {rangeLabel}
        </span>
      </div>
      <div className="kpi-grid" style={{ marginBottom: 22 }}>
        {/* Renewed patients / revenue */}
        <div className="kpi-card" style={{ '--kpi-accent': 'var(--blue-700)', '--kpi-bg': 'var(--blue-100)' }}>
          <div className="kpi-top"><div className="kpi-icon"><Users size={18} /></div></div>
          <div className="kpi-value">{isLoading ? kpiSkeleton : (data?.renewedPatients ?? 0).toLocaleString()}</div>
          <div className="kpi-label">Renewed Patients</div>
        </div>
        <div className="kpi-card" style={{ '--kpi-accent': 'var(--green-700)', '--kpi-bg': 'var(--green-100)' }}>
          <div className="kpi-top"><div className="kpi-icon"><IndianRupee size={18} /></div></div>
          <div className="kpi-value">{isLoading ? kpiSkeleton : fmtCurrency(data?.renewedRevenue)}</div>
          {/* This figure is Σ AmountPaid on renewal invoices, not the billed total — "Revenue",
              matching how the Renewals report tab labels the identical GrossRenewalRevenue figure. */}
          <div className="kpi-label">Renewed Revenue</div>
        </div>
        {KPI_DEFS.map(({ key, label, icon: Icon, color, bg, currency }) => (
          <div key={key} className="kpi-card" style={{ '--kpi-accent': color, '--kpi-bg': bg }}>
            <div className="kpi-top"><div className="kpi-icon"><Icon size={18} /></div></div>
            <div className="kpi-value">
              {isLoading ? kpiSkeleton : currency ? fmtCurrency(data?.[key]) : (data?.[key] ?? 0).toLocaleString()}
            </div>
            <div className="kpi-label">{label}</div>
          </div>
        ))}
      </div>

      {/* ── Charts — both use dr.range directly ── */}
      <div className="g-2" style={{ marginBottom: 18 }}>
        <div className="card">
          <div className="card-header">
            <h3>Revenue</h3>
            <span className="td-muted" style={{ fontSize: '0.76rem' }}>{rangeLabel}</span>
          </div>
          <div className="card-body">
            <ChartBox height={280}>
              <LineChart data={data?.revenueByMonth ?? []}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                <XAxis dataKey="label" stroke="var(--text-muted)" fontSize={11} />
                <YAxis stroke="var(--text-muted)" fontSize={11} tickFormatter={fmtCurrency} />
                <Tooltip formatter={fmtCurrency} contentStyle={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 8 }} />
                <Legend />
                <Line type="monotone" dataKey="billed" stroke="var(--blue-500)" strokeWidth={2.5} dot={{ r: 3 }} />
                <Line type="monotone" dataKey="paid"   stroke="var(--green-600)" strokeWidth={2.5} dot={{ r: 3 }} />
              </LineChart>
            </ChartBox>
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <h3>Lead Funnel</h3>
            <span className="td-muted" style={{ fontSize: '0.76rem' }}>{rangeLabel}</span>
          </div>
          <div className="card-body">
            <ChartBox height={280}>
              <BarChart data={funnelData ?? data?.leadFunnel ?? []} layout="vertical" margin={{ left: 50 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                <XAxis type="number" stroke="var(--text-muted)" fontSize={11} />
                <YAxis type="category" dataKey="label" stroke="var(--text-muted)" fontSize={11} width={140} />
                <Tooltip contentStyle={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 8 }} />
                <Bar dataKey="count" fill="var(--blue-700)" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ChartBox>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h3>Lead Sources &amp; Conversion</h3>
          <span className="td-muted" style={{ fontSize: '0.76rem' }}>{rangeLabel}</span>
        </div>
        <div className="tbl-wrap" style={{ borderRadius: 0, border: 'none' }}>
          <table>
            <thead>
              <tr><th>Source</th><th>Total</th><th>Converted</th><th>Conversion rate</th></tr>
            </thead>
            <tbody>
              {(sourcesData ?? data?.leadSources ?? []).map((s) => (
                <tr key={s.source}>
                  <td className="td-bold">{s.source}</td>
                  <td>{s.total}</td>
                  <td>{s.converted}</td>
                  <td>{(s.conversionRate * 100).toFixed(1)}%</td>
                </tr>
              ))}
              {!isLoading && !sourcesLoading && !(sourcesData ?? data?.leadSources)?.length && (
                <tr><td colSpan={4} style={{ textAlign: 'center', padding: 24, color: 'var(--text-muted)' }}>
                  No lead source data for this period.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showReminder && <SendReminderModal onClose={() => setShowReminder(false)} />}
    </>
  );
}

// ── WhatsApp "Send Reminder" modal ─────────────────────────────────────────
// Builds the exact message string each patient will receive.
const buildMessage = (name, time, doctor) =>
  `Hello ${name},\n\nThis is a gentle reminder about your appointment today at ${time} with Dr. ${doctor} at The Healing Clinic.\n\nPlease arrive 10 minutes early and carry any previous reports.\n\nThank you!\n— The Healing Clinic`;

function SendReminderModal({ onClose }) {
  const [sent, setSent] = useState(false);
  const [result, setResult] = useState({ sent: 0, failed: 0, processed: 0 });
  const [expandedId, setExpandedId] = useState(null);
  const [sendDueReminders, { isLoading: sending }] = useSendDueRemindersMutation();

  const handleSend = async () => {
    try {
      const r = await sendDueReminders().unwrap();
      setResult({ sent: r?.sent ?? 0, failed: r?.failed ?? 0, processed: r?.processed ?? 0 });
      setSent(true);
      // "processed" only means these appointments were checked, not that WhatsApp delivered
      // anything — surface real failures instead of a blanket success toast.
      if (r?.failed > 0) toast.error(`${r.failed} reminder${r.failed !== 1 ? 's' : ''} failed to send — check Communications for details.`);
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Could not send reminders'));
    }
  };

  const todayRange = useMemo(() => {
    const d = new Date();
    const from = new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0).toISOString();
    const to   = new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59).toISOString();
    return { from, to };
  }, []);

  const { data: apptData, isFetching } = useListAppointmentsQuery(
    { from: todayRange.from, to: todayRange.to, pageSize: 100 },
  );

  // Client-side guardrail: only Scheduled / Confirmed appointments for today.
  const targets = useMemo(() => {
    const today = new Date();
    return (apptData?.items ?? []).filter((a) => {
      const apptDate = new Date(a.scheduledAt ?? a.startTime ?? a.date);
      return apptDate.toDateString() === today.toDateString()
        && (a.status === 'Scheduled' || a.status === 'Confirmed');
    });
  }, [apptData]);

  const fmtTime = (d) => d
    ? new Date(d).toLocaleTimeString('en-IN', { timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit' })
    : '—';

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 620 }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <MessageSquare size={18} color="var(--green-600)" /> Send WhatsApp Reminders
          </h2>
          <button type="button" className="modal-close" onClick={onClose}><X size={18} /></button>
        </div>

        <div className="modal-body">
          {sent ? (
            <div style={{ textAlign: 'center', padding: '28px 0' }}>
              {result.failed > 0 ? (
                <AlertTriangle size={52} color="var(--amber-600)" style={{ margin: '0 auto 14px' }} />
              ) : (
                <CheckCircle2 size={52} color="var(--green-600)" style={{ margin: '0 auto 14px' }} />
              )}
              <div style={{ fontWeight: 700, fontSize: '1.05rem', marginBottom: 6 }}>
                {result.failed > 0 ? 'Some reminders failed' : 'Reminders Sent!'}
              </div>
              <div className="td-muted" style={{ fontSize: '0.84rem' }}>
                {result.sent} sent
                {result.failed > 0 && <span style={{ color: 'var(--red-700)' }}>, {result.failed} failed</span>}
                {' '}out of {result.processed} today's Scheduled patient{result.processed !== 1 ? 's' : ''} checked
                (already-sent ones are skipped automatically).
                {result.failed > 0 && (
                  <div style={{ marginTop: 6 }}>
                    Check Communications → Message log (filter by status "Failed") for the reason.
                  </div>
                )}
              </div>
            </div>
          ) : (
            <>
              <div style={{ marginBottom: 14, fontSize: '0.83rem', color: 'var(--text-secondary)' }}>
                A personalised reminder will be sent to each patient below.
                Click any row to preview their exact message.
                Only <strong>Scheduled</strong> appointments for today are included.
              </div>

              {isFetching ? (
                <div className="skeleton" style={{ height: 100, borderRadius: 8 }} />
              ) : targets.length === 0 ? (
                <div style={{ padding: '20px 0', textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.84rem' }}>
                  No Scheduled appointments found for today — no reminders will be sent.
                </div>
              ) : (
                <div style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden', maxHeight: 380, overflowY: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.83rem' }}>
                    <thead>
                      <tr style={{ background: 'var(--surface-alt, var(--gray-50))', position: 'sticky', top: 0, zIndex: 1 }}>
                        <th style={{ padding: '8px 14px', textAlign: 'left', fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>#</th>
                        <th style={{ padding: '8px 14px', textAlign: 'left', fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-muted)' }}>Patient</th>
                        <th style={{ padding: '8px 14px', textAlign: 'left', fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>Appt. Time</th>
                        <th style={{ padding: '8px 14px', textAlign: 'left', fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-muted)' }}>Doctor</th>
                        <th style={{ padding: '8px 14px', textAlign: 'center', fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {targets.map((a, idx) => {
                        const name   = a.patientName ?? '—';
                        const time   = fmtTime(a.scheduledAt ?? a.startTime);
                        const doctor = a.assignedUserName ?? a.doctorName ?? '—';
                        const msg    = buildMessage(name, time, doctor);
                        const isOpen = expandedId === a.id;
                        return (
                          <React.Fragment key={a.id}>
                            <tr
                              style={{ borderTop: '1px solid var(--border)', cursor: 'pointer', background: isOpen ? 'var(--blue-50)' : undefined }}
                              onClick={() => setExpandedId(isOpen ? null : a.id)}
                              title="Click to preview the message this patient will receive"
                            >
                              <td style={{ padding: '9px 14px', color: 'var(--text-muted)', fontWeight: 600 }}>{idx + 1}</td>
                              <td style={{ padding: '9px 14px', fontWeight: 700 }}>{name}</td>
                              <td style={{ padding: '9px 14px', color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>{time}</td>
                              <td style={{ padding: '9px 14px', color: 'var(--text-secondary)' }}>Dr. {doctor}</td>
                              <td style={{ padding: '9px 14px', textAlign: 'center' }}>
                                <span className="badge b-green">{a.status}</span>
                              </td>
                            </tr>
                            {isOpen && (
                              <tr style={{ background: 'var(--blue-50)' }}>
                                <td colSpan={5} style={{ padding: '0 14px 12px 14px' }}>
                                  <div style={{
                                    background: '#e7f5e7',
                                    border: '1px solid #b2dfb2',
                                    borderRadius: 10,
                                    borderTopLeftRadius: 0,
                                    padding: '10px 13px',
                                    fontSize: '0.8rem',
                                    lineHeight: 1.65,
                                    whiteSpace: 'pre-wrap',
                                    color: '#1a1a1a',
                                  }}>
                                    {msg}
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>

        <div className="modal-footer">
          <button type="button" className="btn btn-outline" onClick={onClose}>{sent ? 'Close' : 'Cancel'}</button>
          {!sent && (
            <button
              type="button"
              className="btn btn-success"
              disabled={targets.length === 0 || isFetching || sending}
              onClick={handleSend}
            >
              <Send size={14} /> {sending ? 'Sending…' : `Send to ${targets.length} patient${targets.length !== 1 ? 's' : ''}`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
